import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
} from '@microsoft/signalr';
import authService from '../components/api-authorization/AuthorizeService';
import { EventVm, MatchHubMessageTypeEnum } from '../utils/api';

/**
 * Callbacks a livescore screen provides to react to remote event mutations
 * broadcast by the backend MatchHub (topic "Event", group = matchId).
 */
export interface MatchEventsHubHandlers {
    onRemoteCreate: (event: EventVm) => void;
    onRemoteUpdate: (event: EventVm) => void;
    onRemoteDelete: (event: EventVm) => void;
    /** Full events + match refetch; called after the initial join and every reconnect. */
    onResync: () => Promise<void>;
}

/**
 * One connection per mounted livescore screen: connect -> JoinGroup(matchId)
 * -> listen on "Event" -> dispatch to handlers. Auto-reconnect re-joins the
 * group and triggers a full resync so nothing missed while offline is lost.
 */
export class MatchEventsHub {
    private connection: HubConnection | null = null;

    private matchId: number | null = null;

    private handlers: MatchEventsHubHandlers | null = null;

    public connectAndJoin = async (
        matchId: number,
        handlers: MatchEventsHubHandlers
    ): Promise<void> => {
        this.matchId = matchId;
        this.handlers = handlers;

        const connection = new HubConnectionBuilder()
            .withUrl('/hubs/match', {
                accessTokenFactory: async (): Promise<string> =>
                    (await authService.getAccessToken()) || '',
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Warning)
            .build();

        connection.on('Event', this.handleMessage);
        connection.onreconnected(async (): Promise<void> => {
            try {
                await this.joinGroup();
                await this.handlers?.onResync();
            } catch {
                // Next reconnect cycle will retry; live messages resume regardless.
            }
        });

        this.connection = connection;

        try {
            await connection.start();
            await this.joinGroup();
            // Catch anything created between the screen's first REST load and
            // the moment the hub connection joined the group.
            await handlers.onResync();
        } catch {
            // ponytail: no manual retry loop; if the initial start fails the
            // screen simply works without live sync until remounted. Upgrade
            // path: retry start() with backoff.
        }
    };

    public disconnect = async (): Promise<void> => {
        const { connection, matchId } = this;
        this.connection = null;
        this.handlers = null;
        this.matchId = null;
        if (!connection) return;
        try {
            if (connection.state === HubConnectionState.Connected && matchId !== null) {
                await connection.invoke('LeaveGroup', matchId.toString());
            }
            await connection.stop();
        } catch {
            // Ignore teardown errors (e.g. already disconnected).
        }
    };

    private joinGroup = async (): Promise<void> => {
        if (!this.connection || this.matchId === null) return;
        await this.connection.invoke('JoinGroup', this.matchId.toString());
    };

    private handleMessage = (message: {
        messageType: MatchHubMessageTypeEnum;
        event?: EventVm;
    }): void => {
        const handlers = this.handlers;
        if (!handlers || !message?.event) return;
        const event = EventVm.fromJS(message.event);
        switch (message.messageType) {
            case MatchHubMessageTypeEnum.CreateEvent:
                handlers.onRemoteCreate(event);
                break;
            case MatchHubMessageTypeEnum.UpdateEvent:
                handlers.onRemoteUpdate(event);
                break;
            case MatchHubMessageTypeEnum.DeleteEvent:
                handlers.onRemoteDelete(event);
                break;
            default:
                // MatchFinished etc. are out of scope (events only).
                break;
        }
    };
}

export const applyRemoteDelete = (events: EventVm[], event: EventVm): EventVm[] =>
    events.filter((e) => e.id !== event.id);
